// components/CardButtonGroup/CardButtonGroupComponent.js
import React, { useState, useEffect, useRef } from 'react';
import { Box, Card, CardContent, Typography, Button } from '@mui/material';
import useEmblaCarousel from 'embla-carousel-react';
import gsap from 'gsap';
import { toSentenceCase } from '../../utils/casing';
import { headingLetterSpacing } from '../../utils/headingTypography';

const CardButtonGroupComponent = ({
  name,
  question,
  onChange,
  onNext,
  setValidationState,
  registerValidation,
  isMobile,
}) => {
  // Start with no selection; do NOT hydrate from question.value/form value
  const [selected, setSelected] = useState('');
  const [animatingIndex, setAnimatingIndex] = useState(null);
  const [cardHeight, setCardHeight] = useState(null);
  const [emblaRef] = useEmblaCarousel({
    align: 'center',
    containScroll: false,
    loop: false,
  });
  const cardRefs = useRef([]);
  const groupRef = useRef(null);

  // If this field instance changes (different name) or options change, reset selection
  useEffect(() => {
    setSelected('');
    // also clear validation on mount/reset
    setValidationState?.(name, !question?.validation?.required);
  }, [
    name,
    question?.options,
    setValidationState,
    question?.validation?.required,
  ]);

  // Register validation that uses local `selected` only
  useEffect(() => {
    const triggerValidation = () => {
      const isValid = !(question.validation?.required && !selected);
      setValidationState?.(name, isValid);
      return isValid;
    };
    registerValidation?.(name, triggerValidation);
  }, [
    name,
    selected,
    registerValidation,
    setValidationState,
    question.validation,
  ]);

  useEffect(() => {
    const measureCardHeight = () => {
      const nextHeight = cardRefs.current.reduce((maxHeight, card) => {
        if (!card) return maxHeight;

        return Math.max(maxHeight, card.scrollHeight || card.offsetHeight || 0);
      }, 0);

      setCardHeight((currentHeight) =>
        currentHeight === nextHeight ? currentHeight : nextHeight
      );
    };

    const frame = window.requestAnimationFrame(measureCardHeight);

    return () => window.cancelAnimationFrame(frame);
  }, [question.options, isMobile]);

  useEffect(() => {
    if (!groupRef.current || typeof ResizeObserver === 'undefined')
      return undefined;

    const observer = new ResizeObserver(() => {
      const nextHeight = cardRefs.current.reduce((maxHeight, card) => {
        if (!card) return maxHeight;

        return Math.max(maxHeight, card.scrollHeight || card.offsetHeight || 0);
      }, 0);

      setCardHeight((currentHeight) =>
        currentHeight === nextHeight ? currentHeight : nextHeight
      );
    });

    observer.observe(groupRef.current);

    return () => observer.disconnect();
  }, [question.options, isMobile]);

  const validate = (value = selected) => {
    const isValid = !(question.validation?.required && !value);
    setValidationState?.(name, isValid);
    return isValid;
  };

  const gs_bounce = (target, strength = 0.85) => {
    const tl = gsap.timeline();
    tl.to(target, {
      scale: strength,
      duration: 0.15,
      ease: 'power3.inOut',
    }).to(target, {
      scale: 1,
      duration: 1.3,
      ease: 'elastic.out(1.75, 0.4)',
    });
    return tl;
  };

  const handleSelect = (value, index) => {
    setAnimatingIndex(index);

    const card = cardRefs.current[index];
    const button = card?.querySelector('button');

    const animations = [];
    if (card) animations.push(gs_bounce(card));
    if (button) animations.push(gs_bounce(button));

    Promise.all(animations);

    setAnimatingIndex(null);
    setSelected(value);

    // Still lift the selection up, but do NOT rehydrate from it on mount
    onChange?.(name, value);

    const isValid = validate(value);
    if (isValid && !question.disableAutoNext) {
      onNext?.();
    }
  };

  const renderCardContent = (option, isSelected, index) => (
    <Card
      ref={(el) => (cardRefs.current[index] = el)}
      elevation={isSelected ? 6 : 2}
      onClick={() => handleSelect(option.value, index)}
      sx={(theme) => ({
        width: '226px',
        height: cardHeight ? `${cardHeight}px` : 'auto',
        minHeight: isMobile ? '240px' : '249px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderRadius: '20px',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        boxShadow: `0px 6px 16px 0px #162F3B1F, 0px 1px 2px 0px #A2ACB11F`, //
        // No green border anymore (always transparent)
        border: `2px solid ${isSelected ? theme.palette.component.valid : 'transparent'}`,
        cursor: 'pointer',
        position: 'relative',
        zIndex: 1,
        '&:hover': {
          transform: 'scale(1.03)',
          boxShadow: '0px 6px 16px 0px #162F3B1F',
          zIndex: 10,
          '& .MuiButton-root': {
            backgroundColor: '#C7CA00',
            color: '#162F3B',
          },
        },
      })}
    >
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          px: '20px',
          py: '24px',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Heading, sans-serif',
            fontWeight: 700,
            fontSize: '1rem',
            lineHeight: '1.5rem',
            letterSpacing: headingLetterSpacing('1rem'),
            textAlign: 'left',
            mb: '12px',
            color: (theme) => theme.palette.component?.title || '#162F3B',
            whiteSpace: 'pre-line',
          }}
        >
          {toSentenceCase(option.title)}
        </Typography>

        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            //  px: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={(theme) => ({
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              textAlign: 'left',
              color: theme.palette.component?.cardText || '#4A5A62',
              whiteSpace: 'pre-line',
            })}
          >
            {option.description}
          </Typography>
        </Box>

        <Button
          variant="contained"
          disableElevation
          onClick={(e) => {
            e.stopPropagation();
            handleSelect(option.value, index);
          }}
          sx={{
            paddingRight: '18px',
            paddingLeft: '18px',
            width: '100%',
            height: '40px',
            backgroundColor:
              animatingIndex === index || selected === option.value
                ? '#C7CA00'
                : '#E3E6E8',
            borderRadius: '18px',
            color: '#162F3B',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: '1rem',
            letterSpacing: '-0.02em',
            textTransform: 'none',
            boxShadow: 'none',
            mt: '16px',
            alignSelf: 'center',
            transition: 'all 0.8s ease',
            '&:hover': {
              backgroundColor:
                animatingIndex === index || selected === option.value
                  ? '#C7CA00'
                  : '#ECECEC',
            },
          }}
        >
          Select
        </Button>
      </CardContent>
    </Card>
  );

  const renderCards = () =>
    question.options.map((option, index) => {
      const isSelected = selected === option.value;
      return (
        <Box key={index}>{renderCardContent(option, isSelected, index)}</Box>
      );
    });

  return isMobile ? (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Box
        ref={groupRef}
        sx={{ width: '100%', maxWidth: '360px', overflow: 'visible' }}
      >
        <Box
          ref={emblaRef}
          className="embla"
          sx={{ overflow: 'visible', width: '100%', position: 'relative' }}
        >
          <Box
            className="embla__container"
            sx={{ display: 'flex', overflow: 'visible', position: 'relative' }}
          >
            {question.options.map((option, index) => {
              const isSelected = selected === option.value;
              return (
                <Box
                  key={index}
                  className="embla__slide"
                  sx={{
                    flex: '0 0 70%',
                    display: 'flex',
                    justifyContent: 'center',
                    boxSizing: 'border-box',
                    overflow: 'visible',
                    position: 'relative',
                  }}
                >
                  {renderCardContent(option, isSelected, index)}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  ) : (
    <Box
      ref={groupRef}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        justifyContent: 'center',
        overflow: 'visible',
      }}
    >
      {renderCards()}
    </Box>
  );
};

export default CardButtonGroupComponent;
